import React from 'react';
import classNames from 'clsx';
import s from './CartItem.scss';
import {classes} from './CartItem.st.css';
import {CartItemThumbnail} from '../CartItemThumbnail/CartItemThumbnail';
import {Name} from './partials/Name/Name';
import {ItemDiscounts} from './partials/ItemDiscounts/ItemDiscounts';
import {Remove} from './partials/Remove/Remove';
import {Quantity} from './partials/Quantity/Quantity';
import {TotalPrice} from './partials/TotalPrice/TotalPrice';
import {OutOfStock} from './partials/OutOfStock/OutOfStock';
import {PaymentTypeLabel} from './partials/PaymentTypeLabel/PaymentTypeLabel';
import {useControllerProps} from '../../../../domain/controllers/ControllerContext';
import {ViolationNotification} from '../../Violations/ViolationNotification/ViolationNotification';
import {Divider} from '@wix/wix-ui-tpa/cssVars';
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor';
import {LineItemModel} from '../../../../domain/models/LineItems/LineItem.model';
import {ViolationModel, ViolationSeverity} from '../../../../domain/models/CartSummary/Violation.model';
import {ItemDetails} from './partials/ItemDetails/ItemDetails';
import {MAX_DESCRIPTION_LINE_LENGTH, NUMBER_OF_OPTIONS_TO_SHOW_BEFORE_COLLAPSE} from '../../../constants';
import {mapLineItemToDetails} from './partials/ItemDetails/mapLineItemToDetails';
import {ItemSubscription} from './partials/ItemSubscription/ItemSubscription';
import {UpsellOptions} from './partials/UpsellOptions/UpsellOptions';
import {PaymentOption} from '../../../../domain/models/LineItems/PaymentConfig.model';
import {UpsellFetchStatus} from '../../../../domain/models/LineItems/UpsellOption.model';
import {SPECS} from '../../../../domain/specs';

export enum CartItemDataHook {
  'Root' = 'CartItemDataHook.root',
  'ComparePriceOnLineItem' = 'CartItemDataHook.comparePriceOnLineItem',
  'Name' = 'CartItemDataHook.name',
  'Options' = 'CartItemDataHook.options',
  'PaymentTypeLabel' = 'CartItemDataHook.paymentTypeLabel',
  'Quantity' = 'CartItemDataHook.quantity',
  'QuantityErrorTooltip' = 'CartItemDataHook.quantityErrorTooltip',
  'OutOfStockRoot' = 'CartItemDataHook.outOfStockRoot',
  'OutOfStockError' = 'CartItemDataHook.outOfStockError',
  'Remove' = 'CartItemDataHook.remove',
  'TotalPrice' = 'CartItemDataHook.totalPrice',
  'Discounts' = 'CartItemDataHook.Discounts',
  'DiscountName' = 'CartItemDataHook.DiscountName',
  'DiscountAmountOff' = 'CartItemDataHook.DiscountAmountOff',
  'DiscountIcon' = 'CartItemDataHook.DiscountIcon',
  'Divider' = 'CartItemDataHook.divider',
  'loadingSpinner' = 'CartItemDataHook.loadingSpinner',
  'SubscriptionFrequency' = 'CartItemDataHook.subscriptionFrequency',
  'Subscription' = 'CartItemDataHook.subscription',
  'totalPriceSkeleton' = 'CartItemDataHook.totalPriceSkeleton',
}

interface CartItemProps {
  lineItem: LineItemModel;
  showQuantityBySettings: boolean;
  showThumbnailBySettings: boolean;
  shouldShowItemInfoBySettings: boolean;
  showDivider: boolean;
  onItemRemoved?: () => void;
  quantityClassName?: string;
  thumbnailClassName?: string;
  thumbnailDimensions?: {width: number; height: number};
  infoClassName?: string;
  hideRemoveButton?: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CartItem: React.FC<CartItemProps> = (props: CartItemProps) => {
  const {
    lineItem,
    showQuantityBySettings,
    showThumbnailBySettings,
    shouldShowItemInfoBySettings,
    showDivider,
    onItemRemoved,
    quantityClassName,
    thumbnailClassName,
    thumbnailDimensions,
    infoClassName,
    hideRemoveButton = false,
  } = props;
  const {
    cartStore: {
      shouldDisplayViolations,
      loadingItems,
      cartSummary,
      isSummaryUpdating,
      upsellOptionsByLineItemId,
      upsellFetchStatus,
      acceptUpsell,
      reportUpsellDropdownOpened,
    },
  } = useControllerProps();

  const {t} = useTranslation();
  const {experiments} = useExperiments();
  const shouldHideQuantity = lineItem.quantityInfo.fixedQuantity || !showQuantityBySettings;
  const shouldShowQuantityBadge = !showQuantityBySettings && !lineItem.quantityInfo.fixedQuantity;

  const hasDiscounts = (lineItem.discounts?.length ?? 0) > 0;
  const shouldShowPaymentTypeLabel =
    lineItem.paymentConfig.paymentOption && lineItem.paymentConfig.paymentOption !== PaymentOption.FULL_PAYMENT_ONLINE;
  const isItemOutOfStock = lineItem.quantityInfo.quantity === 0;

  const lineUpsellOptions = upsellOptionsByLineItemId[lineItem.id] ?? [];
  const shouldShowUpsells =
    experiments.enabled(SPECS.ecomCartLineItemUpsells) &&
    Boolean(lineItem.source.upsellOptionsAppId) &&
    !lineItem.attributes.subscriptionInfo &&
    (upsellFetchStatus === UpsellFetchStatus.LOADING || lineUpsellOptions.length > 0);

  const getLineItemViolationIfItExists = (): ViolationModel | undefined => {
    if (shouldDisplayViolations) {
      return (
        cartSummary?.customViolations.find(
          (violation) =>
            violation.target?.lineItemTarget?.id === lineItem.id && violation.severity === ViolationSeverity.error
        ) ??
        cartSummary?.customViolations.find(
          (violation) =>
            violation.target?.lineItemTarget?.id === lineItem.id && violation.severity === ViolationSeverity.warning
        )
      );
    }
  };

  const lineItemViolation = getLineItemViolationIfItExists();
  const shouldShowLineItemViolation = !isItemOutOfStock && Boolean(lineItemViolation);
  const isItemLoading = isSummaryUpdating && loadingItems.includes(lineItem.id);
  const hasModifiersPrice = lineItem.pricing.breakdown.totalModifiersPrice?.convertedAmount !== 0;
  const priceOnShowMore = hasModifiersPrice
    ? lineItem.pricing.breakdown.totalModifiersPrice?.formattedConvertedAmount
    : undefined;

  return (
    <>
      <div className={s.itemWrapper} data-hook={CartItemDataHook.Root}>
        <div className={s.item}>
          <div className={s.info}>
            {showThumbnailBySettings && (
              <CartItemThumbnail
                lineItem={lineItem}
                className={thumbnailClassName}
                dimensions={thumbnailDimensions}
                quantityBadge={shouldShowQuantityBadge ? lineItem.quantityInfo.quantity : undefined}
              />
            )}
            <div className={classNames(s.details, shouldShowUpsells && s.detailsWithUpsell)}>
              <div className={s.content}>
                <Name lineItem={lineItem} />
                {hasDiscounts && <ItemDiscounts lineItem={lineItem} />}
                {shouldShowPaymentTypeLabel && <PaymentTypeLabel lineItem={lineItem} />}
                {
                  <div className={classes.itemData}>
                    {lineItem.attributes.subscriptionInfo && (
                      <ItemSubscription
                        subscription={lineItem.attributes.subscriptionInfo}
                        dataHook={CartItemDataHook.Subscription}
                      />
                    )}
                    {shouldShowItemInfoBySettings && (
                      <ItemDetails
                        dataHook={CartItemDataHook.Options}
                        itemId={lineItem.id}
                        details={mapLineItemToDetails(lineItem, t)}
                        maxOptionValLength={MAX_DESCRIPTION_LINE_LENGTH}
                        infoClassName={infoClassName}
                        collapseSettings={{
                          numberOfOptionsToCollapseAfter: NUMBER_OF_OPTIONS_TO_SHOW_BEFORE_COLLAPSE,
                          showMoreTitle: t('cart.lineItems.showMore', {modifierTotal: priceOnShowMore}),
                          showLessTitle: t('cart.lineItems.showLess'),
                        }}
                      />
                    )}
                  </div>
                }
              </div>
              {
                <div className={s.priceAndQuantity}>
                  {!shouldHideQuantity && <Quantity lineItem={lineItem} className={quantityClassName} />}
                  <TotalPrice lineItem={lineItem} isItemLoading={isItemLoading} />
                </div>
              }
              {shouldShowUpsells && (
                <div className={s.upsellSlot}>
                  <UpsellOptions
                    options={lineUpsellOptions}
                    isLoading={upsellFetchStatus === UpsellFetchStatus.LOADING}
                    disabled={isItemLoading}
                    onOpen={() => reportUpsellDropdownOpened(lineItem, lineUpsellOptions)}
                    onSelect={(option) => acceptUpsell(lineItem, lineUpsellOptions, option)}
                  />
                </div>
              )}
            </div>
          </div>
          {!hideRemoveButton && (
            <div className={s.colEnd}>
              <Remove lineItem={lineItem} onItemRemoved={onItemRemoved} />
            </div>
          )}
        </div>
        {isItemOutOfStock && <OutOfStock />}
        {shouldShowLineItemViolation && (
          <div className={s.violation}>
            <ViolationNotification violation={lineItemViolation} />
          </div>
        )}
      </div>
      {showDivider ? <Divider className={classes.divider} data-hook={CartItemDataHook.Divider} /> : null}
    </>
  );
};
